import React, { useEffect, useState, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { isAuthenticated } from '../../../../services/auth';
import moment from 'moment';
import throttle from 'lodash/throttle';
import { message, Popconfirm } from 'antd';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button as MUIButton, Autocomplete, TextField } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { MUIPagination } from '../../../../components/ui/pagination';
import { addLocation, allLocations, deleteLocation, allCities, citiesByState } from '../../../../services/admin';
import UnifiedDashboard from '../../../../components/layout/unified-dashboard';

const AddLocation = () => {
  const history = useHistory();
  const [name, setName] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [cities, setCities] = useState([]);
  const countPerPage = 5;
  const mountedRef = useRef(true);
  const [locations, setLocations] = useState({
    location: [],
    pagination: [],
    deleted: false,
    newAdded: false,
  });
  const [current, setCurrent] = useState();
  const [search, setSearch] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const { location, pagination, deleted, newAdded } = locations;

  const BRAZILIAN_STATES = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  const { token } = isAuthenticated();

  const searchData = useRef(
    throttle(async (val) => {
      const query = val.toLowerCase();
      setCurrent(1);
      const data = await allLocations();
      const newData = data.data
        .filter((item) => item.name.toLowerCase().indexOf(query) > -1)
        .slice(0, countPerPage);
      if (mountedRef.current) {
        setLocations({ ...locations, pagination: newData });
      }
    }, 400)
  );

  useEffect(() => {
    loadCities();
    if (!search) {
      loadLocations(1);
    } else {
      searchData.current(search);
    }

    return () => {
      mountedRef.current = false;
    };
  }, [search]);

  useEffect(() => {
    if (newAdded || deleted) {
      loadLocations(1);
    }
  }, [newAdded, deleted]);

  useEffect(() => {
    if (state) {
      loadCitiesByState(state);
      setCity(''); // Reset city when state changes
    } else {
      loadCities();
      setCity(''); // Reset city when no state
    }
  }, [state]);

  const loadCities = async () => {
    const res = await allCities();
    if (res.status === 200 && res.data) {
      setCities(res.data);
    }
  };

  const loadCitiesByState = async (selectedState) => {
    try {
      const res = await citiesByState(selectedState);
      if (res.status === 200 && res.data) {
        setCities(res.data);
      }
    } catch (error) {
      console.error('Erro ao buscar cidades:', error);
    }
  };

  const loadLocations = async (page) => {
    const res = await allLocations();
    if (mountedRef.current) {
      setCurrent(page);
      const to = page * countPerPage;
      const from = to - countPerPage;
      setLocations({ location: res.data, pagination: res.data.slice(from, to), newAdded: false, deleted: false });
    }
  };

  const handleDelete = async (locationId) => {
    const res = await deleteLocation(locationId, token);
    if (res.error) {
      console.log(res.error);
    } else {
      console.log(res);
      message.success('Localização excluída', 4);
      setLocations({ ...locations, deleted: true });
    }
  };

  const handleChange = (e) => {
    setName(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!name || !state || !city) {
        message.error('Nome, estado e cidade são obrigatórios');
        return;
      }
      const res = await addLocation({ name, city });
      console.log(res);
      message.success('Localização adicionada', 4);
      setName('');
      setState('');
      setCity('');
      setLocations({ ...locations, newAdded: true });
    } catch (err) {
      console.log(err);
      if (err.response.status === 400) message.error(err.response.data, 4);
    }
  };

  return (
    <UnifiedDashboard title="Gerenciar Localizações">
      <div className='admin-page-container'>
        {/* Header */}
        <div className='admin-page-header'>
          <div className='admin-page-header-content'>
            <h2 className='admin-page-title'>
              <i className='fas fa-map-marker-alt'></i>
              Gerenciar Localizações
            </h2>
            <p className='admin-page-subtitle'>Administre as localizações do sistema</p>
          </div>
        </div>

        {/* Add Form Card */}
        <div className='admin-card'>
          <div className='admin-card-header'>
            <h3 className='admin-card-title'>
              <i className='fas fa-plus-circle'></i>
              Nova Localização
            </h3>
          </div>
          <div className='admin-card-body'>
            <form onSubmit={handleSubmit} className='admin-form'>
              <div className='admin-form-group'>
                <label className='admin-form-label'>Estado</label>
                <Autocomplete
                  options={BRAZILIAN_STATES}
                  value={state}
                  onChange={(event, newValue) => {
                    setState(newValue || '');
                  }}
                  renderInput={(params) => <TextField {...params} className='admin-form-input' required />}
                  disableClearable
                />
              </div>

              <div className='admin-form-group'>
                <label className='admin-form-label'>Cidade</label>
                <Autocomplete
                  options={cities}
                  getOptionLabel={(option) => option.name || ''}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  value={cities.find(c => c._id === city) || null}
                  onChange={(event, newValue) => setCity(newValue ? newValue._id : '')}
                  disabled={!state}
                  renderInput={(params) => <TextField {...params} className='admin-form-input' required />}
                  renderOption={(props, option) => (
                    <li {...props} key={option._id}>
                      {option.name}
                    </li>
                  )}
                  noOptionsText="Nenhuma cidade encontrada"
                  loadingText="Carregando..."
                />
              </div>

              <div className='admin-form-group'>
                <label className='admin-form-label'>Nome da Localização</label>
                <input
                  type='text'
                  className='admin-form-input'
                  placeholder='Ex: São Paulo, Rio de Janeiro...'
                  value={name}
                  onChange={handleChange}
                  required
                />
              </div>
              <button type='submit' className='admin-form-submit-btn'>
                <i className='fas fa-plus'></i> Adicionar Localização
              </button>
            </form>
          </div>
        </div>

        {/* List Card */}
        <div className='admin-card'>
          <div className='admin-card-header'>
            <h3 className='admin-card-title'>
              <i className='fas fa-list'></i>
              Localizações Registradas
            </h3>
            <div className='admin-search-box'>
              <i className='fas fa-search'></i>
              <input
                type='text'
                className='admin-search-input'
                placeholder='Buscar localização...'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className='admin-card-body'>
            {pagination && pagination.length > 0 ? (
              <>
                <div className='admin-table-wrapper'>
                  <table className='admin-table'>
                    <thead>
                      <tr>
                        <th><i className='fas fa-map-pin'></i> Nome</th>
                        <th><i className='fas fa-flag'></i> Estado</th>
                        <th><i className='fas fa-calendar'></i> Data</th>
                        <th className='text-center'><i className='fas fa-cogs'></i> Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagination.map((c, i) => (
                        <tr key={i}>
                          <td>{c.name}</td>
                          <td><span className='admin-badge'>{c.city?.state}</span></td>
                          <td>{moment(c.createdAt).format('DD/MM/YYYY')}</td>
                          <td className='admin-table-actions'>
                            <MUIButton
                              startIcon={<EditIcon />}
                              className='admin-btn admin-btn-edit'
                              onClick={() => history.push(`/admin/location/edit/${c._id}`)}
                              title='Editar'
                              sx={{ mr: 1 }}
                            >
                              Editar
                            </MUIButton>
                            <MUIButton
                              startIcon={<DeleteIcon />}
                              className='admin-btn admin-btn-delete'
                              onClick={() => { setSelectedLocation(c); setDeleteDialog(true); }}
                              title='Excluir'
                            >
                              Excluir
                            </MUIButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className='admin-pagination'>
                  <MUIPagination
                    count={Math.ceil(location.length / countPerPage)}
                    page={current}
                    onChange={(e, page) => loadLocations(page)}
                    color="primary"
                    size="medium"
                    showFirstButton={false}
                    showLastButton={false}
                  />
                </div>
              </>
            ) : (
              <div className='admin-empty-state'>
                <i className='fas fa-inbox'></i>
                <p>Nenhuma localização encontrada</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
      >
        <DialogTitle>Excluir localização</DialogTitle>
        <DialogContent>
          <p>Tem certeza que deseja excluir {selectedLocation?.name}? Esta ação não pode ser desfeita.</p>
        </DialogContent>
        <DialogActions>
          <MUIButton onClick={() => setDeleteDialog(false)}>Cancelar</MUIButton>
          <MUIButton onClick={() => { 
            handleDelete(selectedLocation._id);
            setDeleteDialog(false);
          }}>Excluir</MUIButton>
        </DialogActions>
      </Dialog>
    </UnifiedDashboard>
  );
};

export default AddLocation;
